// Council's hands-free mic: a transcription-only Realtime session (POST /api/live/session {mode:'transcribe'})
// over the same WebRTC transport as live voice. Every finished utterance becomes one council turn.
// The mic is muted while a deity is speaking so the room never transcribes its own voices.
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { createVoiceTransport } from '../live';
import type { LiveConnState, VoiceTransport } from '../live/VoiceTransport';

export function useCouncilEar({ onUtterance, hold }: { onUtterance: (text: string) => void; /** true while deities talk */ hold: boolean }) {
  const [state, setState] = useState<LiveConnState>('idle');
  const [hearing, setHearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useRef<VoiceTransport | null>(null);
  const gen = useRef(0);
  const cb = useRef(onUtterance);
  cb.current = onUtterance;
  const holdRef = useRef(hold);
  holdRef.current = hold;

  const stop = useCallback(() => {
    gen.current++;
    const cur = t.current;
    t.current = null;
    cur?.disconnect();
    setHearing(false);
    setState('idle');
  }, []);

  const start = useCallback(async () => {
    const id = ++gen.current;
    t.current?.disconnect();
    setError(null);
    setState('connecting');
    const tr = createVoiceTransport();
    t.current = tr;
    try {
      const creds = await apiFetch<{ clientSecret: string; model: string }>('/api/live/session', {
        method: 'POST',
        body: JSON.stringify({ mode: 'transcribe' }),
      });
      if (id !== gen.current) return;
      await tr.connect(creds, {
        onState: (s, err) => {
          if (id !== gen.current) return;
          setState(s);
          if (s === 'connected') tr.setMuted(holdRef.current);
          if (s === 'error') setError(err ?? 'Mic failed');
        },
        onSpeaking: () => undefined,
        onToolCall: () => undefined,
        onUserSpeaking: (v) => id === gen.current && setHearing(v),
        onTranscript: (role, text, final) => {
          if (id !== gen.current || role !== 'user' || !final) return;
          const clean = text.trim();
          if (clean.length > 1) cb.current(clean);
        },
      });
    } catch (e) {
      if (id !== gen.current) return;
      t.current = null;
      tr.disconnect();
      setError(e instanceof Error ? e.message : 'Mic failed');
      setState('error');
    }
  }, []);

  // Deities talking -> mic off; their turn over -> mic back on.
  useEffect(() => {
    t.current?.setMuted(hold);
    if (hold) setHearing(false);
  }, [hold]);

  useEffect(() => () => stop(), [stop]);

  const on = state === 'connecting' || state === 'connected' || state === 'reconnecting';
  return { state, on, hearing, error, start, stop };
}
